<template>
    <div id="bookingWidget">
        <Accordion v-model="steps[0]" :enabled="enabledSteps[0]" @nextAction="goTo(1)" :nextDisabled="!enabledSteps[1]">
            <template v-slot:header>
                <i class="fas fa-check-circle" v-if="hasSelectedDates"></i>
                <b>{{ $t('components.booking.common.step') }} 1:</b> {{ $t('components.booking.steps.first.title') }}
                <div class="pull-right" style="padding-right: 5px" v-if="hasSelectedDates">
                    {{ computeStepOneTitle }}
                </div>
            </template>
            <template v-slot:content>
                <div id="availabilityContainer" class="d-flex justify-content-center">
                    <CoreDatePicker 
                        v-model="selectedDates"
                        :bookings="listing.bookings"
                        :columns="$screens({ default: 1, lg: 4 })"
                        :rows="$screens({ default: 1, lg: 1})" 
                        is-inline>
                    </CoreDatePicker>
                </div>
            </template>

        </Accordion>
        <Accordion v-model="steps[1]" :enabled="enabledSteps[1]" @nextAction="goTo(2)" :nextDisabled="!enabledSteps[2]">
            <template v-slot:header>
                <i class="fas fa-check-circle" v-if="hasSelectedOccupants"></i>
                <b>{{ $t('components.booking.common.step') }} 2:</b> {{ $t('components.booking.steps.second.title') }}
                <div class="pull-right" style="padding-right: 5px" v-if="hasSelectedOccupants">
                        {{ computeStepTwoTitle }}
                </div>
            </template>

            <template v-slot:content>
                <form>
                    <div class="form-row">
                        <div class="col-12 col-md-6">
                            <Stepper v-model="adults" title="Number of adults" :minValue="0" />
                        </div>

                        <div class="col-12 col-md-6">
                            <Stepper v-model="kids" title="Number of kids" :minValue="0" />
                        </div>
                    </div>
                </form>

            </template>

        </Accordion>
        <Accordion v-model="steps[2]" :enabled="enabledSteps[2]" :hasNext="false">
            <template v-slot:header>
                <b>{{ $t('components.booking.common.step') }} 3:</b> {{ $t('components.booking.steps.third.title') }}
            </template>

            <template v-slot:content>
                <div class="row">    
                    <!-- Price result -->
                    <div class="col-12">
                        <StaticPriceResult 
                            v-if="hasSelectedRequirements"
                            :startDate="selectedDates.start"
                            :endDate="selectedDates.end"
                            :adults="adults"
                            :kids="kids"
                            :listing="listing"
                        >
                            
                        <div class="call-to-action-btn btn btn-lg btn-success btn-block" @click="initiateBooking(result)">{{ $t('components.booking.offer.contact') }}</div>
                                
                        </StaticPriceResult>
                    </div>    
                </div>
            </template>
        </Accordion>
    </div>
</template>

<script>
import Accordion from "@/components/common/Accordion"
import AlertBox from "@/components/common/AlertBox"
import Stepper from "@/components/common/Stepper";
import StaticPriceResult from "@/components/level3/StaticPriceResult";
import CoreDatePicker from "@/components/common/datepicker/CoreDatePicker";

export default {
    name: "StaticBookingWidget",
    components: {
        Accordion,
        AlertBox,
        Stepper,
        StaticPriceResult,
        CoreDatePicker
    },
    props: {
        listing: {
            type: Object,
            required: true
        }
    },
    computed: {
        listingId: function() {
            return this.listing.id;
        },
        query: function() {
            return this.$store.getters.getQuery;
        },
        hasSelectedDates: function() {
            return !this.isUndefined(this.selectedDates) && !this.allFieldsAreUndefined(this.selectedDates);
        },
        computeStepOneTitle: function() {
            return this.$moment(this.selectedDates.start).format("DD.MM.YYYY") + " - " + this.$moment(this.selectedDates.end).format("DD.MM.YYYY")
        },

        hasSelectedOccupants: function() {
            return !this.isUndefined(this.adults) && this.adults !== 0;
        },

        computeStepTwoTitle: function() {
            return [
                this.computeText(this.adults, this.$i18n.tc('common.generic.adult', this.adults)),
                this.computeText(this.kids, this.$i18n.tc('common.generic.kid', this.kids))
            ].filter(val => val !== "").join(", ");
        },
        result: function() {
            console.log("Computing result")
            return {
                    startDate: this.selectedDates.start,
                    endDate: this.selectedDates.end,
                    "numberOfAdults": this.adults,
                    "numberOfKids": this.kids
                }
        },
        enabledSteps: function() {
            return [
                true,
                this.hasSelectedDates,
                this.hasSelectedOccupants
            ]
        },
        hasSelectedRequirements: function() {
            return !(
                this.isUndefined(this.selectedDates) 
                || this.isUndefined(this.selectedDates.start) 
                || this.isUndefined(this.selectedDates.end) 
                || this.isUndefined(this.adults))
        },
        selectedDates: {
            get() {
                return this.$store.getters.getQuery.selectedDates;
            },
            set(val){
                // Update the query
                let query = this.getEditable(this.$store.getters.getQuery);

                query.selectedDates = val;

                this.$store.commit('setQuery', query);

            }
        },
        adults: {
            get() {
                return this.$store.getters.getQuery.numberOfAdults;
            },
            set(val) {
                let query = this.getEditable(this.$store.getters.getQuery);

                query.numberOfAdults = val;

                this.$store.commit('setQuery', query);
            }
        },
        kids: {
            get() {
                return this.$store.getters.getQuery.numberOfKids;
            },
            set(val) {
                let query = this.getEditable(this.$store.getters.getQuery);

                query.numberOfKids = val;

                this.$store.commit('setQuery', query);
            }
        }
    },
    methods: {
        goTo: function(idx) {
            if (this.enabledSteps[idx]) {
                this.$set(this.steps, idx - 1, false);
                this.$set(this.steps, idx, true)
            }
        },
        computeText: function(value, str) {
            if (value == 0) {
                return "";
            }

            return value + " " + str
        },
        initiateBooking: function(offer) {
            this.$router.push(`/${this.listing.objectId}/booking`)
        }
    },
    created() {
        // Setup the steps
        this.steps = [];
        for (let i = 0; i < this.totalSteps; i++) {
            // We start with all closed
            this.steps.push(false);
        }

        // For some reason, the kids sometimes do not allow to click on next...just check if we set them correctly
        if (this.isUndefined(this.kids)) {
            console.log("Kids are undefined.")
            this.kids = 0;
        }
    },
    mounted() {
        // On mount, we set the actual values
        this.$set(this.steps, 0, !this.hasSelectedDates);
        this.$set(this.steps, 1, !this.hasSelectedOccupants && this.hasSelectedDates);
        this.$set(this.steps, 2, this.hasSelectedDates && this.hasSelectedOccupants);
    },
    data() {
        return {
            totalSteps: 3,
            steps: [],
        }
    }
}
</script>

<style lang="scss" scoped>

    .card {
        margin-top: 0px;
    }

    .card-header {
        color: white;
        background-color: $primary-color !important;
        border-color: transparent;
    }

    .card-title > * {
        color: white !important;
        padding: 0;
        margin: 0;
    }
</style>